import React, {HTMLAttributes, useEffect, useState} from 'react';
import {Controller, useFieldArray, get, useWatch} from 'react-hook-form';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner, faPlusCircle} from '@fortawesome/free-solid-svg-icons';
import SubHeader from 'app/gakki/components/SubHeader';
import Block from './block';
import {IconButton} from '../../../../../components/Button';
import {
  Input,
} from 'components/form-items';
import Select from 'components/Select';
import { SalaryCategory, SalaryAccount, SubjectCategory, DefaultBasicSubjectCode, defaultBasicSubjectCodes, foodAllowanceUpperLimit } from '@gakki/api/types';
import {ReactComponent as EditIcon} from 'app/gakki/assets/edit.svg';
import {ReactComponent as TrashIcon} from 'app/gakki/assets/trash.svg';
import { formatCurrency } from '@gakki/utils/format';

export const SubjectBlock: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={`w-full border border-[#d5dadd] rounded-[3px] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

export const SubjectHead: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={`h-[50px] flex items-center bg-[#eff0f0] border-b border-[#d5dadd] gap-2.5 px-2.5 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

export const SubjectRow: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={`h-[70px] flex items-center border-b border-b-[#d5dadd] last:border-b-0 gap-2.5 px-2.5 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

const SubjectError: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={`absolute text-xs w-full left-0 bottom-[-17px] text-[#ff3b30] ${className}`}
      style={{ wordBreak: 'keep-all' }}
      {...props}
    >
      {children}
    </div>
  );
};

export const subjectDataName = `payrollSubject.payrollSubjectItems`;
const SubjectData = function (props: any) {
  const {
    formProps,
    defaultValues,
    options,
    updateLocale,
    resetSubjectState,
    openSalarySubjectForm,
    initMonthlySubject,
    initHourlySubject
  } = props;
  const [resetSubject, setResetSubject] = resetSubjectState
  const {control, errors, formState} = formProps;
  const [subjectTotal, setSubjectTotal] = useState(0);
  const currencyformater = new Intl.NumberFormat('zh-TW', {
    style: 'currency',
    currency: 'TWD',
    minimumFractionDigits: 0,
  });
  const salaryNamePrefix = `personalPayroll`;
  const isEnable = useWatch({
    control,
    name: `${salaryNamePrefix}.enabled`,
    defaultValue: defaultValues.enabled,
  });
  const salaryCategoryValue = useWatch({
    control,
    name: `${salaryNamePrefix}.salaryCategory`,
    defaultValue: defaultValues.salaryCategory,
  });
  const basicSalaryCategoryId = options.accountCategory.find((c: any) => c.code === SubjectCategory.basicSalary)?.id
  const currentSalarySubject =
    salaryCategoryValue === SalaryCategory.hourly
      ? options.salaryAccounts.filter((s: SalaryAccount) => s.hnSalaryAccountCode !== DefaultBasicSubjectCode.baseSalary && s.hnSalaryAccountCode !== DefaultBasicSubjectCode.foodAllowance)
      : options.salaryAccounts.filter((s: SalaryAccount) => s.hnSalaryAccountCode !== DefaultBasicSubjectCode.hourlySalary || s.salaryAccountCategoryId !== basicSalaryCategoryId);
  const subjectField = useFieldArray({
    control: formProps.control,
    name: subjectDataName,
    keyName: 'subjectId',
  });
  const subjectFieldValue = useWatch({
    control,
    name: subjectDataName,
    defaultValue: subjectField.fields,
  });
  const calculateSubjectTotal = (value?: string, index?: number) => {
    setSubjectTotal(
      subjectFieldValue.reduce((acc: any, f: any, idx: number) => {
        if (idx === index) {
          return acc + Number(value ? value : 0);
        }
        return acc + Number(f.money ? f.money : 0);
      }, 0),
    );
  };
  const isError = (name: string) =>
    isEnable && formState.isSubmitted && get(errors, name);
  const addSubject = () => {
    subjectField.append({
      salaryAccountName: null,
      salaryAccountCode: null,
      salaryAccountId: null,
      money: '',
    });
    updateLocale();
  };

  useEffect(() => {
    if (subjectField.fields.length > 0) {
      calculateSubjectTotal();
    }
  }, [subjectField]);

  useEffect(() => {
    if (resetSubject) {
      if (subjectField.fields.length > 0) {
        clearSubjectFields();
      } else {
        if (salaryCategoryValue === SalaryCategory.hourly) {
          formProps.setValue(subjectDataName, initHourlySubject());
        } else {
          formProps.setValue(subjectDataName, initMonthlySubject());
        }
        setResetSubject(false);
      }
    }
  }, [resetSubject, subjectField]);

  const clearSubjectFields = () => {
    new Array(subjectField.fields.length)
    .fill(0)
    .forEach(() => subjectField.remove(0));
  }
  return (
    <div className="w-[365px] flex-none" data-test-id={'subject-data'}>
      <SubHeader
        rightSide={
          <IconButton
            onClick={e => {
              e.preventDefault();
              openSalarySubjectForm();
            }}
            disabled={false}
            data-test-id={'open-account-btn'}
          >
            <EditIcon/>
            薪資科目設定
          </IconButton>
        }
      >
      <span style={{paddingRight: '15px'}}>個人薪資科目</span>
      </SubHeader>
      <Block padding="15px" className="h-[calc(100%-80px)]">
        <SubjectBlock>
          <SubjectHead>
            <div style={{width: '175px'}}>
              薪資科目
            </div>
            <div
              style={{
                width: 'auto',
                fontSize: '15px',
                color: '#26ABE3',
                fontWeight: 500,
              }}
            >
              {currencyformater.format(subjectTotal)}
            </div>
          </SubjectHead>
          {subjectField.fields.map((f: any, index: number) => {
            const isBasicSalaryCategory = f.salaryAccountCategoryId === basicSalaryCategoryId
            return (
              <SubjectRow key={f.subjectId} data-test-id={`subject-${index}`}>
                <input
                  name={`${subjectDataName}.${index}.hnSalaryAccountCode`}
                  hidden
                  ref={formProps.register()}
                  defaultValue={f.hnSalaryAccountCode}
                />
                <input
                  name={`${subjectDataName}.${index}.salaryAccountCategoryId`}
                  hidden
                  ref={formProps.register()}
                  defaultValue={f.salaryAccountCategoryId}
                />
                <div
                  style={{
                    width: '175px',
                    position: 'relative',
                  }}
                  data-test-id={'salary-account-code'}
                >
                  <Controller
                    render={({onChange, value}) => (
                      <Select
                        value={currentSalarySubject.find(
                          (o: any) => o.salaryAccountId === value,
                        )}
                        options={currentSalarySubject}
                        onChange={(o: any) => {
                          onChange(o.salaryAccountId);
                          updateLocale();
                        }}
                        isDisabled={
                          defaultBasicSubjectCodes.includes(f.hnSalaryAccountCode) ||
                          isBasicSalaryCategory ||
                          formState.isSubmitting ||
                          !isEnable
                        }
                        getOptionLabel={(o: any) => `${o.salaryAccountCode} ${o.salaryAccountName}`}
                        getOptionValue={(o: any) => o.salaryAccountId}
                        filterOption={(option: any) => {
                          return (
                            option.value === value ||
                            !subjectFieldValue.some(
                              (f: any) => f.salaryAccountId === option.value,
                            )
                          );
                        }}
                        error={Boolean(
                          formState.isSubmitted &&
                            get(
                              errors,
                              `${subjectDataName}.${index}.salaryAccountId`,
                            ),
                        )}
                      />
                    )}
                    name={`${subjectDataName}.${index}.salaryAccountId`}
                    control={control}
                    rules={{
                      required: '科目為必填',
                      validate: {
                        subjectUnexist: v =>
                        !currentSalarySubject.find(
                          (o: any) => o.salaryAccountId === v,
                        )
                            ? '薪資科目不存在'
                            : true,
                      },
                    }}
                    defaultValue={f.salaryAccountId}
                  />
                  {get(
                    errors,
                    `${subjectDataName}.${index}.salaryAccountId`,
                  ) && (
                    <SubjectError>
                      {
                        get(
                          errors,
                          `${subjectDataName}.${index}.salaryAccountId`,
                        )?.message
                      }
                    </SubjectError>
                  )}
                </div>
                <div
                  style={{
                    width: '100px',
                    position: 'relative',
                  }}
                  data-test-id={'money'}
                >
                  <Controller
                    render={({onChange, value}) => (
                      <Input
                        style={{boxSizing: 'border-box', width: '100%' }}
                        value={value}
                        disabled={formState.isSubmitting || !isEnable}
                        onChange={e => {
                          const {value} = e.target;
                          if (!value || /^\d+$/.test(value)) {
                            onChange(value);
                            calculateSubjectTotal(value, index);
                            updateLocale();
                          }
                        }}
                        maxLength={9}
                        error={isError(`${subjectDataName}.${index}.money`)}
                      />
                    )}
                    name={`${subjectDataName}.${index}.money`}
                    control={control}
                    rules={{
                      required: isEnable ? '金額為必填' : false,
                      validate: {
                        foodAllowanceLimit: v =>
                          f.hnSalaryAccountCode === DefaultBasicSubjectCode.foodAllowance &&
                          v > foodAllowanceUpperLimit
                            ? `伙食津貼上限${formatCurrency(foodAllowanceUpperLimit)}`
                            : true,
                      },
                    }}
                    defaultValue={f.money}
                  />
                  {isError(`${subjectDataName}.${index}.money`) && (
                    <SubjectError>
                      {
                        get(errors, `${subjectDataName}.${index}.money`)
                          ?.message
                      }
                    </SubjectError>
                  )}
                </div>
                <div
                  style={{
                    width: '20px',
                    display:
                      subjectField.fields.length < 1 ||
                      defaultBasicSubjectCodes.includes(f.hnSalaryAccountCode) ||
                      isBasicSalaryCategory
                        ? 'none'
                        : 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      subjectField.remove(index);
                      calculateSubjectTotal('0', index);
                      updateLocale();
                    }}
                    disabled={formState.isSubmitting || !isEnable}
                    data-test-id={'remove-btn'}
                  >
                    <TrashIcon/>
                  </IconButton>
                </div>
              </SubjectRow>
            );
          })}
          { salaryCategoryValue === SalaryCategory.monthly &&
            <div className="h-10 flex justify-end">
              <IconButton
                onClick={addSubject}
                disabled={formState.isSubmitting || !isEnable}
                data-test-id={'add-btn'}
              >
                <FontAwesomeIcon icon={faPlusCircle} style={{fontSize: 28}} />
              </IconButton>
            </div>
          }
        </SubjectBlock>
      </Block>
    </div>
  );
};

export default SubjectData;
